import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  Button,
  ButtonLink,
  getBtnClassName,
} from "@/components/Interface/Button/Button";
import { Modal } from "@/components/Interface/Modal/Modal";
import { ButtonGroupFlexBox } from "@/components/Layout/ButtonGroupFlexBox/ButtonGroupFlexBox";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { useGetIdentificationURL } from "@/services/api";
import { SimCustomerInfoAddESIMNotAllowedReasonItem } from "@/services/model";
import { AddDeviceBlockedReason } from "@/utils/customerUtils";

type Props = {
  reasons: AddDeviceBlockedReason[];
  onClose: () => void;
};

const reasonTranslationKey = (
  reason: AddDeviceBlockedReason,
): string | null => {
  switch (reason) {
    case SimCustomerInfoAddESIMNotAllowedReasonItem.TooManySIMs:
      return "portal:add-device.simSelection.reasons.TooManySIMs";
    case SimCustomerInfoAddESIMNotAllowedReasonItem.IDCheckOpen:
      return "portal:add-device.simSelection.reasons.IDCheckOpen";
    case SimCustomerInfoAddESIMNotAllowedReasonItem.DunningLevel3:
      return "portal:add-device.simSelection.reasons.DunningLevel3";
    case SimCustomerInfoAddESIMNotAllowedReasonItem.DunningProcessOpen:
      return "Onboarding.sections.sim-selection.dunningProcessOpenText";
    default:
      return null;
  }
};

export const AddDeviceBlockedModal = ({ reasons, onClose }: Props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const hasTooManySims = reasons.includes(
    SimCustomerInfoAddESIMNotAllowedReasonItem.TooManySIMs,
  );
  const hasIdCheckOpen = reasons.includes(
    SimCustomerInfoAddESIMNotAllowedReasonItem.IDCheckOpen,
  );
  const hasDunning =
    reasons.includes(
      SimCustomerInfoAddESIMNotAllowedReasonItem.DunningLevel3,
    ) ||
    reasons.includes(
      SimCustomerInfoAddESIMNotAllowedReasonItem.DunningProcessOpen,
    );

  const { data: identificationUrl } = useGetIdentificationURL({
    query: {
      enabled: hasIdCheckOpen,
      retry: false,
      staleTime: 5 * 60 * 1000,
    },
  });

  const identificationUrlWithLanguage = useMemo(() => {
    if (!identificationUrl) {
      return undefined;
    }

    const url = new URL(identificationUrl);
    url.searchParams.set("lang", i18n.resolvedLanguage ?? "en");

    return url.toString();
  }, [identificationUrl, i18n.resolvedLanguage]);

  const translatedReasons = reasons
    .map((reason) => {
      const key = reasonTranslationKey(reason);

      return key ? t(key as any) : null;
    })
    .filter((text): text is string => text !== null);

  const handleViewBills = () => {
    navigate(portalRoutes.documents.bills, { replace: true });
  };

  const externalActionClassName = getBtnClassName({
    variant: "secondary",
    inverted: true,
    fullWidth: true,
    className: "tablet:w-fit",
  });

  const contextualActions = (
    <>
      {hasTooManySims && (
        <a
          className={externalActionClassName}
          href={t("Onboarding.sections.sim-selection.upgradeSimLimitLink")}
          target="_blank"
          rel="noreferrer"
        >
          <span className="btn-bg" aria-hidden="true" />
          <span className="btn-label">
            {t("Onboarding.sections.sim-selection.upgradeSimLimit")}
          </span>
        </a>
      )}
      {hasDunning && (
        <Button
          className="tablet:w-fit"
          fullWidth
          variant="secondary"
          inverted
          onClick={handleViewBills}
        >
          {t("portal:add-device.blockedModal.viewBills")}
        </Button>
      )}
      {hasIdCheckOpen &&
        (identificationUrlWithLanguage ? (
          <a
            className={externalActionClassName}
            href={identificationUrlWithLanguage}
            target="_blank"
            rel="noreferrer"
          >
            <span className="btn-bg" aria-hidden="true" />
            <span className="btn-label">
              {t("portal:cockpit.id-banner.open.button")}
            </span>
          </a>
        ) : (
          <ButtonLink
            className="tablet:w-fit"
            fullWidth
            variant="secondary"
            inverted
            to={portalRoutes.cockpit.idCheck}
          >
            {t("portal:cockpit.id-banner.open.button")}
          </ButtonLink>
        ))}
    </>
  );

  const hasContextualActions = hasTooManySims || hasDunning || hasIdCheckOpen;

  const closeButton = (
    <Button variant="primary" inverted onClick={onClose}>
      {t("portal:add-device.blockedModal.close")}
    </Button>
  );

  return (
    <Modal
      title={t("portal:common.tooltip.actionNotPossible.title")}
      onClose={onClose}
      hasScroll={false}
      variant="dark"
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <p className="text-body-base text-foreground-primary-inverse">
            {t("portal:common.tooltip.actionNotPossible.text", {
              count: translatedReasons.length,
            })}
          </p>
          {translatedReasons.length > 0 && (
            <ul className="list-outside list-disc space-y-2 pl-4 text-body-base text-foreground-primary-inverse">
              {translatedReasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          )}
        </div>

        <ButtonGroupFlexBox
          className="!mt-0"
          rightAligned
          forceButtonWidth
          primaryButton={closeButton}
          secondaryButton={
            hasContextualActions ? (
              <div className="flex flex-col gap-3 tablet:flex-row tablet:flex-wrap tablet:gap-4 tablet:[&>div]:w-auto">
                {contextualActions}
              </div>
            ) : undefined
          }
        />
      </div>
    </Modal>
  );
};
